import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lightning, ArrowLeft, ShieldCheck, Rocket, Users, CurrencyCircleDollar } from "@phosphor-icons/react";

/**
 * Layout compartilhado para Login e Register.
 * Split-screen no desktop, empilhado no mobile.
 * Painel visual (esquerda) tem:
 *  - Marca
 *  - Value props rotativas com Framer Motion
 *  - Grid + orbes iluminados (auth-visual class)
 *
 * Painel de formulário (direita) recebe children.
 */
const VALUE_PROPS = [
  {
    Icon: ShieldCheck,
    title: "Banco digital sob sua marca",
    sub: "Pix em tempo real via Mercado Pago. Sem cartório, sem fila, sem drama.",
  },
  {
    Icon: Rocket,
    title: "Esteira de vendas embutida",
    sub: "Funil com vídeo, planos e área de membros — tudo num link só.",
  },
  {
    Icon: Users,
    title: "Afiliados até 50%",
    sub: "Você define quanto paga. Split automático. Zero repasse manual.",
  },
  {
    Icon: CurrencyCircleDollar,
    title: "Taxa transparente",
    sub: "Só cobra quando o dinheiro entra. Sem mensalidade. Sem surpresa.",
  },
];

export default function AuthSplitScreen({ children, side = "right" }) {
  return (
    <div
      className="min-h-screen-safe w-full grid lg:grid-cols-2 relative overflow-hidden bg-[var(--bg-base)]"
      data-testid="auth-shell"
    >
      {/* Painel visual */}
      <aside
        className={`auth-visual relative hidden lg:flex flex-col justify-between p-10 xl:p-14 ${
          side === "right" ? "order-1" : "order-2"
        }`}
      >
        {/* Marca */}
        <Link to="/" className="flex items-center gap-2 z-10 w-fit group" data-testid="auth-logo">
          <Lightning size={26} weight="fill" color="var(--accent)" />
          <span className="font-display font-black text-xl tracking-tight text-white group-hover:text-[var(--accent)] transition-colors">
            MERCADOPAGU
          </span>
        </Link>

        {/* Value props em cascade */}
        <div className="relative z-10 space-y-6 max-w-md">
          <div className="label-caps">Por que MercadoPagu</div>
          {VALUE_PROPS.map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className="flex items-start gap-3"
            >
              <div className="w-9 h-9 rounded-lg bg-[var(--accent)]/10 border border-[var(--accent)]/30 flex items-center justify-center shrink-0">
                <v.Icon size={18} color="var(--accent)" weight="duotone" />
              </div>
              <div className="min-w-0">
                <div className="text-white font-display font-bold text-base">{v.title}</div>
                <div className="text-sm text-white/60 leading-relaxed wrap-safe">{v.sub}</div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Footer */}
        <div className="relative z-10 flex items-center gap-4 text-[11px] text-white/40 font-mono">
          <span>bacen · mp · sslabs A+</span>
          <span className="w-px h-3 bg-white/15" />
          <span>© 2026 MercadoPagu</span>
        </div>

        {/* Orbes iluminados decorativos */}
        <motion.div
          aria-hidden
          className="absolute -bottom-32 -right-20 w-96 h-96 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(255,179,15,0.35) 0%, transparent 60%)",
            filter: "blur(40px)",
          }}
          animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.8, 0.5] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
      </aside>

      {/* Painel do formulário */}
      <main
        className={`relative flex flex-col min-h-screen-safe px-5 sm:px-8 md:px-12 lg:px-16 py-8 lg:py-12 ${
          side === "right" ? "order-2" : "order-1"
        }`}
      >
        {/* Marca visível em mobile (o aside é hidden lg:flex) */}
        <div className="flex items-center justify-between lg:hidden mb-8">
          <Link to="/" className="flex items-center gap-2" data-testid="auth-logo-mobile">
            <Lightning size={22} weight="fill" color="var(--accent)" />
            <span className="font-display font-black text-lg tracking-tight text-white">
              MERCADOPAGU
            </span>
          </Link>
          <Link
            to="/"
            className="text-xs text-white/60 hover:text-white flex items-center gap-1"
            data-testid="auth-back-home"
          >
            <ArrowLeft size={12} /> Voltar
          </Link>
        </div>

        {/* Voltar (desktop) */}
        <Link
          to="/"
          className="hidden lg:inline-flex items-center gap-1 text-xs text-white/40 hover:text-white transition-colors w-fit mb-8"
          data-testid="auth-back-home-desktop"
        >
          <ArrowLeft size={12} /> voltar ao site
        </Link>

        {/* Conteúdo (form) — centralizado verticalmente */}
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md mx-auto lg:mx-0">{children}</div>
        </div>
      </main>
    </div>
  );
}
